const IMAGE_WIDTH = 1920;
const STRIP_WIDTH = 64;
const SAMPLE_HEIGHT = 256;
const STOP_COUNT = 5;
const SMOOTHING_RADIUS = 48;
const controllers = new WeakMap();

export function projectHeroImage({ source, slot, fit = 'cover', position = {} }) {
    if (
        ![source.width, source.height, slot.width, slot.height].every(
            (size) => Number.isFinite(size) && size > 0,
        )
    )
        return null;
    const scale = (fit === 'contain' ? Math.min : Math.max)(
        slot.width / source.width,
        slot.height / source.height,
    );
    const width = source.width * scale;
    const height = source.height * scale;
    const focus = (value) => Math.max(0, Math.min(100, Number.isFinite(value) ? value : 50)) / 100;
    return {
        x: (slot.width - width) * focus(position.x),
        y: (slot.height - height) * focus(position.y),
        width,
        height,
    };
}

export function heroEdgeGradient(pixels, { width, height, startColumn, stripWidth = STRIP_WIDTH }) {
    const stops = Array.from({ length: STOP_COUNT }, (_, stop) => {
        const row = Math.round(((height - 1) * stop) / (STOP_COUNT - 1));
        const channels = [0, 0, 0];
        let totalWeight = 0;
        // Broad color regions keep foliage and small highlights from producing
        // a striped background. Nearby rows carry more weight than distant ones.
        for (
            let y = Math.max(0, row - SMOOTHING_RADIUS);
            y <= Math.min(height - 1, row + SMOOTHING_RADIUS);
            y += 1
        ) {
            const weight = SMOOTHING_RADIUS + 1 - Math.abs(y - row);
            for (let x = startColumn; x < Math.min(width, startColumn + stripWidth); x += 1) {
                const offset = (y * width + x) * 4;
                channels[0] += pixels[offset] * weight;
                channels[1] += pixels[offset + 1] * weight;
                channels[2] += pixels[offset + 2] * weight;
                totalWeight += weight;
            }
        }
        return `rgb(${channels.map((sum) => Math.round(sum / totalWeight)).join(' ')}) ${Number(((stop * 100) / (STOP_COUNT - 1)).toFixed(2))}%`;
    });
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
}

function imagePosition(value) {
    const parts = value.trim().split(/\s+/);
    const percentage = (part) => {
        if (part === 'left' || part === 'top') return 0;
        if (part === 'right' || part === 'bottom') return 100;
        return part?.endsWith('%') ? Number.parseFloat(part) : 50;
    };
    return { x: percentage(parts[0]), y: percentage(parts[1]) };
}

function backgroundColors(media, view) {
    const colors = [];
    for (let node = media; node; node = node.parentElement) {
        const color = view.getComputedStyle(node).backgroundColor;
        if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') colors.push(color);
    }
    return colors.reverse();
}

function bitmapSize(image) {
    const source = image.currentSrc || image.src;
    const sources = [...(image.closest('picture')?.querySelectorAll('source') || []), image];
    for (const element of sources) {
        for (const candidate of (element.srcset || '').split(',')) {
            const [url, descriptor = '1x'] = candidate.trim().split(/\s+/);
            if (!url) continue;
            let matches;
            try {
                matches = new URL(url, image.baseURI).href === source;
            } catch {
                continue;
            }
            if (!matches) continue;
            const value = Number.parseFloat(descriptor);
            if (Number.isFinite(value) && value > 0) {
                // naturalWidth/Height are density-corrected for srcset; source
                // rectangles in drawImage use the decoded bitmap's pixel units.
                // Width descriptors describe the selected bitmap, while crop-
                // aware sizes can request more than the capped 1920px slot.
                if (descriptor.endsWith('w')) {
                    return {
                        width: value,
                        height: Math.ceil((value * image.naturalHeight) / image.naturalWidth),
                    };
                }
                return {
                    width: Math.ceil(image.naturalWidth * value),
                    height: Math.ceil(image.naturalHeight * value),
                };
            }
        }
    }
    return { width: image.naturalWidth, height: image.naturalHeight };
}

function sampleImageEdges(image, slot, projection, backgrounds) {
    const canvas = image.ownerDocument.createElement('canvas');
    canvas.width = STRIP_WIDTH * 2;
    canvas.height = SAMPLE_HEIGHT;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    // Composite alpha and contain letterboxes against the rendered backdrop.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    backgrounds.forEach((color) => {
        context.fillStyle = color;
        context.fillRect(0, 0, canvas.width, canvas.height);
    });
    const pixelsSize = bitmapSize(image);
    const verticalScale = SAMPLE_HEIGHT / slot.height;
    for (let side = 0; side < 2; side += 1) {
        const start = side === 0 ? 0 : slot.width - STRIP_WIDTH;
        const left = Math.max(start, projection.x);
        const right = Math.min(start + STRIP_WIDTH, projection.x + projection.width);
        const top = Math.max(0, projection.y);
        const bottom = Math.min(slot.height, projection.y + projection.height);
        if (right <= left || bottom <= top) continue;
        // Read only the source regions visible in the two edge windows. A full
        // photo draw with clipping can still rasterize far more source pixels.
        // The projection retains the configured cover/contain crop and focus.
        context.drawImage(
            image,
            ((left - projection.x) / projection.width) * pixelsSize.width,
            ((top - projection.y) / projection.height) * pixelsSize.height,
            ((right - left) / projection.width) * pixelsSize.width,
            ((bottom - top) / projection.height) * pixelsSize.height,
            side * STRIP_WIDTH + left - start,
            top * verticalScale,
            right - left,
            (bottom - top) * verticalScale,
        );
    }
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    return {
        left: heroEdgeGradient(pixels, {
            width: canvas.width,
            height: canvas.height,
            startColumn: 0,
        }),
        right: heroEdgeGradient(pixels, {
            width: canvas.width,
            height: canvas.height,
            startColumn: STRIP_WIDTH,
        }),
    };
}

function initializeHeroEdges(hero) {
    if (controllers.has(hero)) return;
    const view = hero.ownerDocument.defaultView;
    const track = hero.querySelector('[data-home-hero-slides]');
    const states = [...hero.querySelectorAll('[data-hero-edge-left]')]
        .map((edge) => ({
            media: edge.parentElement,
            image: edge.parentElement.querySelector('[data-hero-desktop-alt]'),
            slideIndex:
                Number(edge.closest('[data-hero-slide-index]')?.dataset.heroSlideIndex) || 0,
            cache: new Map(),
            failedSource: '',
            layoutKey: '',
            mediaWidth: 0,
            slot: { width: 0, height: 0 },
        }))
        .filter(({ image }) => image);
    if (!view || !states.length) return;
    const observedStates = new Map(
        states.flatMap((state) => [
            [state.media, state],
            [state.image, state],
        ]),
    );
    let frame = 0;
    let idle = 0;
    let visible = false;
    let disposed = false;
    let activeRange = { first: 0, last: 0 };
    const nativeIdle = typeof view.requestIdleCallback === 'function';
    const clear = (state) => {
        delete state.media.dataset.heroEdgeReady;
        state.layoutKey = '';
    };
    const refresh = (state) => {
        const { media, image, mediaWidth, slot } = state;
        if (mediaWidth <= IMAGE_WIDTH || !image.complete || !image.naturalWidth) {
            clear(state);
            return;
        }
        const source = image.currentSrc || image.src;
        if (state.failedSource === source) return;
        const style = view.getComputedStyle(image);
        const projection = projectHeroImage({
            source: { width: image.naturalWidth, height: image.naturalHeight },
            slot,
            fit: style.objectFit,
            position: imagePosition(style.objectPosition),
        });
        if (!projection) return;
        const backgrounds = backgroundColors(media, view);
        const key = JSON.stringify([
            source,
            image.naturalWidth,
            image.naturalHeight,
            slot,
            projection,
            backgrounds,
        ]);
        if (state.layoutKey === key) return;
        let colors = state.cache.get(key);
        if (!colors) {
            try {
                colors = sampleImageEdges(image, slot, projection, backgrounds);
            } catch {
                // Cross-origin images without readable pixels keep the ordinary
                // theme backdrop; never hide the photo or trigger another load.
                state.failedSource = source;
                clear(state);
                return;
            }
            if (!colors) return;
            if (state.cache.size >= 4) state.cache.delete(state.cache.keys().next().value);
            state.cache.set(key, colors);
        }
        media.style.setProperty('--z-hero-edge-left', colors.left);
        media.style.setProperty('--z-hero-edge-right', colors.right);
        media.dataset.heroEdgeReady = 'true';
        state.layoutKey = key;
    };
    const cancel = () => {
        if (frame) view.cancelAnimationFrame(frame);
        if (idle) {
            if (nativeIdle) view.cancelIdleCallback(idle);
            else view.clearTimeout(idle);
        }
        frame = 0;
        idle = 0;
    };
    const disconnect = () => {
        disposed = true;
        observer.disconnect();
        visibilityObserver.disconnect();
        cancel();
        states.forEach(({ image, onLoad, onError }) => {
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
        });
        track?.removeEventListener('zirbelino:slider-prepare', onPrepare);
        track?.removeEventListener('zirbelino:slider-change', onSlideChange);
        controllers.delete(hero);
    };
    const run = () => {
        idle = 0;
        if (!hero.isConnected) {
            disconnect();
            return;
        }
        if (visible && !disposed) {
            states.forEach((state) => {
                if (state.slideIndex >= activeRange.first && state.slideIndex <= activeRange.last)
                    refresh(state);
            });
        }
    };
    const schedule = () => {
        if (disposed || !visible || frame || idle) return;
        frame = view.requestAnimationFrame(() => {
            // Initial and resize decoration work follows the photo's paint.
            frame = view.requestAnimationFrame(() => {
                frame = 0;
                if (!hero.isConnected) {
                    disconnect();
                    return;
                }
                if (!visible || disposed) return;
                idle = nativeIdle
                    ? view.requestIdleCallback(run, { timeout: 500 })
                    : view.setTimeout(run, 0);
            });
        });
    };
    const onSlideChange = (event) => {
        const first = Number(event.detail?.firstSlideIndex);
        const last = Number(event.detail?.lastSlideIndex);
        if (Number.isInteger(first) && first >= 0) {
            activeRange = { first, last: Number.isInteger(last) && last >= first ? last : first };
        }
        schedule();
    };
    const onPrepare = (event) => {
        if (disposed) return;
        const state = states.find(
            ({ image }) => image.closest('[data-hero-slide-index]') === event.detail?.slide,
        );
        if (!state) return;
        // The image barrier already decoded this target. Prepare its palette
        // synchronously so the same frame reveals both photo and gradient.
        // A decorative failure must never stop an otherwise usable slide.
        try {
            refresh(state);
        } catch {
            clear(state);
        }
    };
    const observer = new view.ResizeObserver((entries) => {
        if (!hero.isConnected) {
            disconnect();
            return;
        }
        // Consume the browser's completed layout before changing any attributes.
        // The sampler also reuses these sizes during synchronous slide prepare.
        entries.forEach(({ target, contentRect }) => {
            const state = observedStates.get(target);
            if (!state) return;
            if (target === state.media) state.mediaWidth = contentRect.width;
            else state.slot = { width: contentRect.width, height: contentRect.height };
        });
        states.forEach((state) => {
            if (state.mediaWidth <= IMAGE_WIDTH) clear(state);
        });
        schedule();
    });
    const visibilityObserver = new view.IntersectionObserver((entries) => {
        if (!hero.isConnected) {
            disconnect();
            return;
        }
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) schedule();
        else cancel();
    });
    states.forEach((state) => {
        state.onLoad = () => {
            state.cache.clear();
            state.failedSource = '';
            state.layoutKey = '';
            schedule();
        };
        state.onError = () => clear(state);
        state.image.addEventListener('load', state.onLoad);
        state.image.addEventListener('error', state.onError);
        observer.observe(state.media);
        observer.observe(state.image);
    });
    visibilityObserver.observe(hero);
    track?.addEventListener('zirbelino:slider-prepare', onPrepare);
    track?.addEventListener('zirbelino:slider-change', onSlideChange);
    controllers.set(hero, { disconnect });
}

export function initHeroEdgeGradients(root = document) {
    if (root.matches?.('[data-hero-edge-gradient]')) initializeHeroEdges(root);
    root.querySelectorAll?.('[data-hero-edge-gradient]').forEach(initializeHeroEdges);
}
